import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, AlertOctagon, Clock, Power, PlayCircle, StopCircle, Server, Activity, List, Info, Maximize2, Search, BarChart2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '5 minutes';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

const BeamIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleDeployment, setExampleDeployment] = useState('');
  const [exampleDeveloper, setExampleDeveloper] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);
  const [pollInterval, setPollInterval] = useState(300);

  const [apiToken, setApiToken] = useState('');
  const [connectPollInterval, setConnectPollInterval] = useState(300);
  const [credentialTab, setCredentialTab] = useState<'console' | 'cli'>('console');

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getBeamStatus();
      if (data?.connected) {
        setConnected(true);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
        setPollInterval(data.poll_interval || 300);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectBeam({ api_token: apiToken, poll_interval: connectPollInterval });
      setSuccessMsg('Beam connected successfully!');
      setApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectBeam();
      setConnected(false);
      setSuccessMsg('Beam disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testBeam();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Beam (${data.deployment_count ?? 0} deployment${data.deployment_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <BeamIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Beam
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Serverless deployment, container/sandbox lifecycle, task monitoring, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Resources Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()} resource{resourcesTracked !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Poll Interval</span>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Beam actions and triggers in your workflows to automate serverless monitoring and incident response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Task Failed', desc: 'Triggers when a Beam task transitions to FAILED.', Icon: AlertOctagon },
                  { name: 'Task Timed Out', desc: 'Triggers when a task transitions to TIMEOUT.', Icon: Clock },
                  { name: 'Container Stopped', desc: 'Triggers when a container, pod, or sandbox leaves a running state.', Icon: StopCircle },
                  { name: 'Deployment Scaled to Zero', desc: 'Triggers when a deployment drops to zero warm containers.', Icon: AlertTriangle },
                  { name: 'Queue Depth High', desc: 'Triggers when a deployment\u2019s task queue backlog grows.', Icon: BarChart2 },
                  { name: 'Machine Down', desc: 'Triggers when a machine leaves a ready state (pool capacity loss).', Icon: Server },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'List Deployments', desc: 'List the deployments in the workspace.', Icon: List },
                  { name: 'Get Deployment', desc: 'Retrieve a deployment\u2019s state and version.', Icon: Info },
                  { name: 'Scale Deployment', desc: 'Set the desired container count for a deployment.', Icon: Maximize2 },
                  { name: 'Start Deployment', desc: 'Activate a stopped deployment.', Icon: PlayCircle },
                  { name: 'Stop Deployment', desc: 'Deactivate a running deployment.', Icon: StopCircle },
                  { name: 'List Containers', desc: 'List running containers, pods, and sandboxes.', Icon: List },
                  { name: 'Get Container Status', desc: 'Retrieve a container or sandbox status.', Icon: Power },
                  { name: 'Stop Container', desc: 'Stop a running container, pod, or sandbox.', Icon: StopCircle },
                  { name: 'List Tasks', desc: 'List recent tasks with their status.', Icon: List },
                  { name: 'Get Task Status', desc: 'Fetch the status and detail of a task.', Icon: Activity },
                  { name: 'List Machines', desc: 'List machines, optionally scoped to a pool.', Icon: Server },
                  { name: 'Investigate Beam', desc: 'AI investigates failing tasks, stuck containers, and scaling issues.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a Beam task fails for the{' '}
              <input
                value={exampleDeployment}
                onChange={e => setExampleDeployment(e.target.value)}
                placeholder="deployment"
                style={{ width: `${Math.max(exampleDeployment.length || 10, 10)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              deployment, investigate the root cause, DM the{' '}
              <input
                value={exampleDeveloper}
                onChange={e => setExampleDeveloper(e.target.value)}
                placeholder="developer"
                style={{ width: `${Math.max(exampleDeveloper.length || 9, 9)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              on Slack with the summary, and create a Jira bug ticket.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleDeployment || !exampleDeveloper}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a Beam task fails for the ${exampleDeployment} deployment, investigate the root cause, DM ${exampleDeveloper} on Slack with the summary, and create a Jira bug ticket.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <BeamIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle>Connect Beam</CardTitle>
                <CardDescription>Connect Beam for serverless deployment, container and sandbox lifecycle, task monitoring, and investigation automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">API Key</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to Beam with a workspace <strong className="text-foreground">API key</strong>, sent as a Bearer token to <strong className="text-foreground">https://app.beam.cloud</strong> to read deployment, container, and task state and perform actions across your workspace. Create one from the Beam dashboard or the CLI.
              </p>
              <div className="inline-flex rounded-md border border-input bg-background p-0.5 mb-3">
                <button
                  type="button"
                  onClick={() => setCredentialTab('console')}
                  className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                    credentialTab === 'console' ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Beam Dashboard
                </button>
                <button
                  type="button"
                  onClick={() => setCredentialTab('cli')}
                  className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                    credentialTab === 'cli' ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  CLI
                </button>
              </div>

              {credentialTab === 'console' ? (
                <div className="rounded-md border border-input bg-muted/40 p-4 mb-3">
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                      <span>In the <strong className="text-foreground">Beam dashboard</strong>, go to <strong className="text-foreground">Settings → API Keys &amp; Workspace ID</strong>.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                      <span>Click <strong className="text-foreground">Create Key</strong>, then copy the generated <strong className="text-foreground">token</strong>.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                      <span>Paste the token below.</span>
                    </li>
                  </ol>
                </div>
              ) : (
                <div className="rounded-md border border-input bg-muted/40 p-3 mb-3 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Install the Beam CLI and create a context — you'll be prompted to paste a token (create one in <strong className="text-foreground">Settings → API Keys &amp; Workspace ID</strong>), which the CLI saves to <code className="bg-background px-1 rounded">~/.beam/config.ini</code>:
                  </p>
                  <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 overflow-x-auto whitespace-pre">{`pip install beam-client
beam config create kestrel`}</pre>
                  <p className="text-xs text-muted-foreground">
                    Then copy the <code className="bg-background px-1 rounded">token</code> value from <code className="bg-background px-1 rounded">~/.beam/config.ini</code> and paste it below.
                  </p>
                </div>
              )}

              <Input
                id="beam-token"
                type="password"
                value={apiToken}
                onChange={e => setApiToken(e.target.value)}
                placeholder="Your Beam API key"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Beam API.
              </p>
            </div>
            <div>
              <Label htmlFor="beam-poll" className="text-sm">Poll Interval</Label>
              <select
                id="beam-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Unlike most integrations, Beam doesn't send tenant-wide outbound webhooks, so Kestrel polls the Beam API to detect task, container, deployment, and machine events. Faster intervals detect incidents sooner; the minimum is 60 seconds.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !apiToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Beam
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BeamIntegration;
